import React, { useState, useEffect } from "react";
import { FaCamera, FaEye, FaEyeSlash, FaTimes } from "react-icons/fa";
import api from "../../services/apiService"; // Adjust path as needed
import { ROLES } from "../../constants";
import { validatePassword, PASSWORD_REQUIREMENTS_MESSAGE } from "../../utils/passwordValidation";

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  role: "user",
  status: "Active",
};

const ROLE_OPTIONS = Object.values(ROLES);
const STATUS_OPTIONS = ["Active", "Inactive"];

const splitDisplayName = (nameValue) => {
  const parts = String(nameValue || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

const mapUserToForm = (userData) => {
  if (!userData) return INITIAL_FORM;

  const fallbackNames = splitDisplayName(userData.Name || userData.name);

  return {
    firstName: userData.firstName || userData.FirstName || fallbackNames.firstName,
    lastName: userData.lastName || userData.LastName || fallbackNames.lastName,
    email: userData.Email || userData.email || "",
    password: "",
    role: userData.Role || userData.role || "user",
    status: String(userData.Status || userData.status || "").toLowerCase() === "active" || userData.isActive
      ? "Active"
      : "Inactive",
  };
};

const AddEmployeeModal = ({ show, handleClose, mode = "add", user = null, onSuccess }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const isEditMode = mode === "edit";

  useEffect(() => {
    if (show) {
      setForm(isEditMode ? mapUserToForm(user) : INITIAL_FORM);
      setShowPassword(false);
      setPasswordError("");
    }
  }, [show, isEditMode, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!isEditMode) {
      const { valid, message } = validatePassword(form.password);
      if (!valid) {
        setPasswordError(message);
        return;
      }
    }
    setPasswordError("");
    setLoading(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        role: form.role,
        isActive: form.status === "Active",
      };

      if (isEditMode) {
        const targetId = user?.id || user?.userId;
        if (!targetId) {
          throw new Error("Missing employee id for edit.");
        }
        const updatePayload = {
          firstName: payload.firstName,
          lastName: payload.lastName,
          email: payload.email,
          role: payload.role,
          isActive: payload.isActive,
        };
        await api.put(`/update-user/${targetId}`, updatePayload);
      } else {
        await api.post("/users/signup", {
          firstName: payload.firstName,
          lastName: payload.lastName,
          email: payload.email,
          password: form.password,
          role: payload.role,
          isActive: payload.isActive,
        });
      }

      setForm(INITIAL_FORM);
      if (onSuccess) onSuccess();
      handleClose();
    } catch (error) {
      console.error(isEditMode ? "Failed to update user:" : "Failed to add user:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 dark:bg-black/60 p-3 sm:p-6">
      <button
        type="button"
        aria-label={isEditMode ? "Close edit user modal" : "Close add user modal"}
        onClick={handleClose}
        className="absolute inset-0"
      />

      <div className="relative w-full max-w-4xl rounded-2xl border border-app-primary bg-app-card shadow-2xl dark:shadow-black/50">
        <div className="flex items-start justify-between border-b border-app-primary px-5 py-4 sm:px-6">
          <div>
            <h3 className="text-xl font-semibold text-app-heading">
              {isEditMode ? "Edit User" : "Add User"}
            </h3>
            <p className="text-sm text-app-muted">
              {isEditMode
                ? "Update user profile details."
                : "Create a new user profile."}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-2 text-app-muted transition-colors hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }} className="max-h-[85vh] overflow-y-auto px-5 py-5 sm:px-6">
          <div className="mb-6 flex justify-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full border border-app-primary bg-app-secondary text-app-muted">
              <FaCamera size={30} />
            </div>
          </div>

          <div className="space-y-6">
            <section>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-app-muted">
                User Information
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="user-first-name" className="mb-1 block text-sm font-medium text-app-secondary">
                    First Name
                  </label>
                  <input
                    id="user-first-name"
                    name="firstName"
                    type="text"
                    className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder="Enter first name"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="user-last-name" className="mb-1 block text-sm font-medium text-app-secondary">
                    Last Name
                  </label>
                  <input
                    id="user-last-name"
                    name="lastName"
                    type="text"
                    className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder="Enter last name"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="user-email" className="mb-1 block text-sm font-medium text-app-secondary">
                    Email
                  </label>
                  <input
                    id="user-email"
                    name="email"
                    type="email"
                    className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Enter email"
                    required
                  />
                </div>

                {!isEditMode && (
                  <div>
                    <label htmlFor="user-password" className="mb-1 block text-sm font-medium text-app-secondary">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="user-password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 pr-11 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                        value={form.password}
                        onChange={handleChange}
                        placeholder="Enter password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 flex items-center px-3 text-app-muted transition-colors hover:text-app-primary"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-app-muted">{PASSWORD_REQUIREMENTS_MESSAGE}</p>
                    {passwordError && (
                      <p className="mt-1 text-xs font-medium text-app-error">{passwordError}</p>
                    )}
                  </div>
                )}

                <div>
                  <label htmlFor="user-role" className="mb-1 block text-sm font-medium text-app-secondary">
                    Role
                  </label>
                  <select
                    id="user-role"
                    name="role"
                    className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    value={form.role}
                    onChange={handleChange}
                    required
                  >
                    {ROLE_OPTIONS.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="user-status" className="mb-1 block text-sm font-medium text-app-secondary">
                    Status
                  </label>
                  <select
                    id="user-status"
                    name="status"
                    className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none transition-colors focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                    value={form.status}
                    onChange={handleChange}
                    required
                  >
                    {STATUS_OPTIONS.map((statusOption) => (
                      <option key={statusOption} value={statusOption}>
                        {statusOption}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>
          </div>

          <div className="sticky bottom-0 mt-6 flex justify-end gap-3 border-t border-app-primary bg-app-card py-4">
            <button type="button" onClick={handleClose} disabled={loading} className="rounded-lg border border-app-primary px-5 py-2 text-sm font-semibold text-app-secondary transition hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="rounded-lg border border-[#2470B7] bg-[#2470B7] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500">
              {loading ? "Saving..." : isEditMode ? "Save Changes" : "Save User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEmployeeModal;
